import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { api, ApiError } from "../lib/api";

interface InviteClub {
  name: string;
  slug: string;
  contact_phone: string | null;
}

export default function ClubInvitePage() {
  const { slug, token: inviteToken } = useParams<{ slug: string; token: string }>();
  const { token, user } = useAuth();

  const [club, setClub] = useState<InviteClub | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [joined, setJoined] = useState<"new" | "already" | "pending" | null>(null);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    password: "",
  });

  const load = useCallback(async () => {
    if (!slug || !inviteToken) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await api.clubs.inviteInfo(slug, inviteToken);
      setClub(resp.data.club);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message || "Invitación no válida."
          : "Error de red. Intenta otra vez.",
      );
    } finally {
      setLoading(false);
    }
  }, [slug, inviteToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const isPlayer = Boolean(token && user?.roles.includes("player"));

  async function handleJoin() {
    if (!token || !slug || !inviteToken) return;
    setSaving(true);
    setError(null);
    try {
      const resp = await api.clubs.inviteJoin(token, slug, inviteToken);
      setJoined(resp.data.already_member ? "already" : resp.data.pending_approval ? "pending" : "new");
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message || "No se pudo unir." : "Error de red.",
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleRegister(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!slug || !inviteToken) return;
    setSaving(true);
    setError(null);
    try {
      const resp = await api.clubs.inviteRegister(slug, inviteToken, {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        password: form.password,
        phone: form.phone.trim() || null,
      });
      setJoined(resp.data.pending_approval ? "pending" : "new");
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message || "No se pudo completar el registro."
          : "Error de red.",
      );
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <section className="card">
        <p className="muted">Cargando invitación…</p>
      </section>
    );
  }

  if (!club) {
    return (
      <section className="card">
        <h1>Invitación no válida</h1>
        <p className="form-error">{error ?? "Revisa el link con tu club."}</p>
      </section>
    );
  }

  if (joined) {
    return (
      <section className="card">
        <p className="eyebrow">¡Listo! 🎾</p>
        <h1>{joined === "already" ? `Ya eres socio de ${club.name}` : joined === "pending" ? `Solicitud enviada a ${club.name}` : `Bienvenido a ${club.name}`}</h1>
        <p className="muted">
          {joined === "already"
            ? "Tu membresía ya estaba activa."
            : joined === "pending"
              ? "El club revisará tu solicitud. Cuando te apruebe quedarás como socio y podrás reservar."
              : "Tu membresía quedó registrada. Pronto podrás reservar canchas desde tu cuenta."}
        </p>
        <Link className="btn" to={token ? "/player" : "/login"}>
          {token ? "Ir a mi inicio" : "Iniciar sesión"}
        </Link>
      </section>
    );
  }

  return (
    <>
      <section className="card">
        <p className="eyebrow">Invitación de club</p>
        <h1>{club.name}</h1>
        <p className="card__subtitle">
          Te invitaron a ser socio de {club.name} en Torneos123.
        </p>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
      </section>

      {isPlayer ? (
        <section className="card">
          <h2 className="card__title">Unirme como {user?.full_name}</h2>
          <p className="muted">
            Tu cuenta de jugador quedará ligada al padrón de {club.name}.
          </p>
          <button
            type="button"
            className="btn"
            onClick={() => void handleJoin()}
            disabled={saving}
          >
            {saving ? "Uniendo…" : `Unirme a ${club.name}`}
          </button>
        </section>
      ) : token ? (
        <section className="card">
          <p className="muted">
            Tu sesión actual no es de jugador. Cierra sesión e inicia con tu
            cuenta de jugador, o regístrate con otro correo.
          </p>
        </section>
      ) : (
        <section className="card">
          <h2 className="card__title">Crear mi cuenta y unirme</h2>
          <p className="muted">
            ¿Ya tienes cuenta?{" "}
            <Link to={`/login?next=/club-invite/${slug}/${inviteToken}`}>
              Inicia sesión
            </Link>{" "}
            y vuelve a abrir este link.
          </p>
          <form className="form" onSubmit={handleRegister}>
            <label>
              Nombre completo
              <input
                type="text"
                required
                value={form.full_name}
                disabled={saving}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              />
            </label>
            <label>
              Email
              <input
                type="email"
                required
                value={form.email}
                disabled={saving}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </label>
            <label>
              Teléfono (WhatsApp)
              <input
                type="text"
                value={form.phone}
                disabled={saving}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </label>
            <label>
              Contraseña
              <input
                type="password"
                required
                minLength={8}
                value={form.password}
                disabled={saving}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </label>
            <button className="btn" type="submit" disabled={saving}>
              {saving ? "Registrando…" : "Registrarme y unirme"}
            </button>
          </form>
        </section>
      )}
    </>
  );
}
